import type { Message } from '../types';
import {
  CURRENT_SESSION_KEY,
  SESSION_DATA_PREFIX,
  SESSIONS_METADATA_KEY,
} from '../constants/storage';

export interface SessionMetadata {
  id: string;
  title: string;
  createdAt: string;
  lastModified: string;
  messageCount: number;
  preview: string;
}

export interface SessionData {
  id: string;
  title: string;
  createdAt: string;
  lastModified: string;
  messages: Message[];
  threadId?: string | null;
}

const PREVIEW_LIMIT = 160;

function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function getSessionKey(id: string): string {
  return `${SESSION_DATA_PREFIX}${id}`;
}

export function loadSessionMetadata(): SessionMetadata[] {
  const storage = getStorage();
  if (!storage) return [];
  const parsed = safeParse<SessionMetadata[]>(storage.getItem(SESSIONS_METADATA_KEY), []);
  return Array.isArray(parsed) ? parsed : [];
}

export function saveSessionMetadata(list: SessionMetadata[]): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(SESSIONS_METADATA_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

export function loadSession(id: string): SessionData | null {
  const storage = getStorage();
  if (!storage) return null;
  return safeParse<SessionData | null>(storage.getItem(getSessionKey(id)), null);
}

export function saveSession(data: SessionData): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(getSessionKey(data.id), JSON.stringify(data));
  } catch {
    // ignore
  }
}

export function deleteSession(id: string): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(getSessionKey(id));
  } catch {
    // ignore
  }
}

export function getCurrentSessionId(): string | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    return storage.getItem(CURRENT_SESSION_KEY);
  } catch {
    return null;
  }
}

export function setCurrentSessionId(id: string | null): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    if (!id) storage.removeItem(CURRENT_SESSION_KEY);
    else storage.setItem(CURRENT_SESSION_KEY, id);
  } catch {
    // ignore
  }
}

export function createSessionId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return `sess_${crypto.randomUUID()}`;
    }
  } catch {
    // ignore
  }
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function deriveSessionTitle(messages: Message[]): string {
  const firstUser = messages.find(msg => msg.sender === 'user' && msg.text && msg.text.trim());
  if (firstUser?.text) return firstUser.text.trim().slice(0, 60);
  return 'New conversation';
}

export function deriveSessionPreview(messages: Message[]): string {
  const combined = messages
    .map(msg => (msg.text || '').trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  return combined.length > PREVIEW_LIMIT ? `${combined.slice(0, PREVIEW_LIMIT)}…` : combined;
}

export function formatSessionDate(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString();
}
